import React from "react";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Dialog from "@mui/material/Dialog";
import CircularProgress from "@mui/material/CircularProgress";

import {
  API_get_pos_link_reconciliation,
  API_link_pos_store_team_member,
} from "../../../../../constants";
import { getDefaultAxiosConfig } from "../../utilities/requests";

const secondaryButton = "rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600 disabled:opacity-50";
const primaryButton = "rounded-lg bg-violet-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-violet-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:cursor-not-allowed disabled:opacity-50";
const employeeName = (employee) => `${employee?.first_name || ""} ${employee?.last_name || ""}`.trim() || "Unnamed employee";
const errorMessage = (err, fallback) => err?.response?.data?.detail || err?.response?.data?.message || fallback;

function EmployeeIdentity({ employee, label }) {
  return (
    <div className="min-w-0">
      {label && <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>}
      <p className="break-words text-sm font-semibold text-slate-900">{employeeName(employee)}</p>
      <p className="mt-1 break-words text-sm text-slate-500">{employee?.email || "No email provided"}</p>
    </div>
  );
}

// A new employee/location gets a fresh session, including in-flight request guards.
export default function ToastEmployeeLinkModal(props) {
  if (!props.open || !props.member) return null;
  return <EmployeeLinkSession key={`${props.storeId}:${props.member.id}:${Boolean(props.member.is_archived)}`} {...props} />;
}

function EmployeeLinkSession({ member, storeId, onClose, onRequestRefresh, onToastLinkUpdate }) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [details, setDetails] = React.useState(null);
  const [actionStatus, setActionStatus] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showAssignList, setShowAssignList] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [selection, setSelection] = React.useState(null);
  const [isReviewing, setIsReviewing] = React.useState(false);
  const alive = React.useRef(true);
  const saving = React.useRef(false);
  const requestVersion = React.useRef(0);
  const callbacks = React.useRef({ onToastLinkUpdate, onRequestRefresh });
  callbacks.current = { onToastLinkUpdate, onRequestRefresh };
  const titleId = React.useId();
  const memberId = member.id;
  const isArchivedSource = Boolean(member.is_archived);

  const fetchDetails = React.useCallback(async () => {
    const version = ++requestVersion.current;
    setIsLoading(true);
    setError(null);
    try {
      const idParam = isArchivedSource ? "archived_store_team_member_id" : "store_team_member_id";
      const config = await getDefaultAxiosConfig();
      const response = await axios.get(
        `${API_get_pos_link_reconciliation}${encodeURIComponent(storeId)}&${idParam}=${encodeURIComponent(memberId)}`,
        config
      );
      if (!alive.current || version !== requestVersion.current) return;
      const payload = response.data?.redux?.payload ?? response.data ?? null;
      setDetails(payload);
      if (payload?.store_team_member) {
        callbacks.current.onToastLinkUpdate?.({
          ...payload.store_team_member,
          pos_store_team_member_uuid: payload?.toast_link?.pos_store_team_member?.uuid ?? null,
        });
      }
    } catch (err) {
      if (!alive.current || version !== requestVersion.current) return;
      setError(errorMessage(err, "We couldn't load the employee connection. Try again."));
      setDetails(null);
    } finally {
      if (alive.current && version === requestVersion.current) setIsLoading(false);
    }
  }, [memberId, storeId, isArchivedSource]);

  React.useEffect(() => {
    alive.current = true;
    fetchDetails();
    return () => { alive.current = false; requestVersion.current += 1; };
  }, [fetchDetails]);

  const currentProfile = details?.toast_link?.pos_store_team_member;
  const isReassigning = Boolean(currentProfile);
  const candidates = isReassigning
    ? (details?.active_unlinked_members || []).filter((candidate) => candidate.permission_type !== "rightwork_internal")
    : (details?.pos_candidates || []);
  const normalizedQuery = query.trim().toLowerCase();
  const filteredCandidates = candidates.filter((candidate) =>
    `${employeeName(candidate)} ${candidate.email || ""}`.toLowerCase().includes(normalizedQuery)
  );
  const canChoose = details && (isReassigning ? showAssignList : !isArchivedSource);
  const close = () => { if (!saving.current) onClose(); };

  const saveConnection = async () => {
    if (saving.current || !selection || !storeId || (!isReassigning && isArchivedSource)) return;
    saving.current = true;
    setIsSaving(true);
    setActionStatus(null);
    try {
      const config = await getDefaultAxiosConfig();
      await axios.post(API_link_pos_store_team_member, {
        pos_store_team_member_uuid: isReassigning ? currentProfile.uuid : selection.uuid,
        store_team_member_id: isReassigning ? selection.id : memberId,
        store_id: storeId,
      }, config);
    } catch (err) {
      if (alive.current) {
        setActionStatus({ type: "error", message: errorMessage(err, "We couldn't save this connection. Please try again.") });
        setIsSaving(false);
      }
      saving.current = false;
      return;
    }
    if (!alive.current) return;
    setActionStatus({ type: "success", message: `Connection saved for ${employeeName(isReassigning ? selection : member)}.` });
    setSelection(null);
    setIsReviewing(false);
    setShowAssignList(false);
    setQuery("");
    // A refresh failure must not present a successful save as a failed mutation.
    await fetchDetails();
    saving.current = false;
    if (alive.current) {
      setIsSaving(false);
      callbacks.current.onRequestRefresh?.();
    }
  };

  return (
    <Dialog open onClose={close} aria-labelledby={titleId} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "16px", margin: "16px", width: "calc(100% - 32px)", maxHeight: "calc(100% - 32px)" } }}>
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 sm:px-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-700"><LinkIcon /></div>
          <div>
            <h2 id={titleId} className="text-lg font-semibold text-slate-900">{isReviewing ? "Review employee connection" : "Employee connection"}</h2>
            <p className="mt-1 text-sm text-slate-500">Connect your point of sale to the right person.</p>
          </div>
        </div>
        <button type="button" aria-label="Close employee connection" disabled={isSaving} onClick={close}
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600 disabled:opacity-50"><CloseIcon fontSize="small" /></button>
      </div>

      <div className="overflow-y-auto px-6 py-5 sm:px-4" aria-busy={isLoading || isSaving}>
        {actionStatus && <div role={actionStatus.type === "error" ? "alert" : "status"} className={`mb-4 rounded-xl border p-4 text-sm ${actionStatus.type === "error" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>{actionStatus.message}</div>}
        {error ? (
          <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            <p>{error}</p><button type="button" onClick={fetchDetails} className={`${secondaryButton} mt-3`}>Try again</button>
          </div>
        ) : isLoading ? (
          <div role="status" className="flex items-center justify-center gap-3 py-16 text-sm text-slate-500"><CircularProgress size={22} color="inherit" />Loading employee connection…</div>
        ) : !details ? (
          <p className="py-8 text-center text-sm text-slate-500">No connection details are available for this employee.</p>
        ) : isReviewing && selection ? (
          <div className="space-y-5">
            <div className="rounded-xl border border-slate-200 p-4"><EmployeeIdentity employee={isReassigning ? currentProfile : selection} label="Point of sale profile" /></div>
            <div className={`grid items-center gap-4 rounded-xl bg-slate-50 p-4 ${isReassigning ? "grid-cols-[1fr_auto_1fr] sm:grid-cols-1" : "grid-cols-1"}`}>
              <EmployeeIdentity employee={member} label={isReassigning ? "Currently connected to" : "Rightwork employee"} />
              {isReassigning && <><ArrowForwardIcon className="text-slate-400 sm:rotate-90" /><EmployeeIdentity employee={selection} label="Move connection to" /></>}
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900">
              <p className="font-semibold">{isReassigning ? "This also moves time punches" : "Check that these are the same person"}</p>
              <p className="mt-1">{isReassigning
                ? `Existing time punches assigned to ${employeeName(member)} will move to ${employeeName(selection)}, and attendance will be recalculated. Only continue if these records belong to the same person or the punches were assigned incorrectly.`
                : "Future imported activity from this profile will be connected to this Rightwork employee. Check their name and email before saving."}</p>
              {isReassigning && <p className="mt-2">Scheduled shifts stay with their current employee. This does not archive or merge the employee accounts.</p>}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <section className="overflow-hidden rounded-xl border border-slate-200">
              <div className="flex items-start justify-between gap-3 p-4"><EmployeeIdentity employee={member} label="Rightwork employee" />{isArchivedSource && <span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600">Archived</span>}</div>
              <div className="border-t border-slate-200 bg-slate-50 p-4">
                {currentProfile ? <><div className="mb-3 flex items-center gap-1.5 text-xs font-medium text-emerald-700"><CheckCircleOutlineIcon fontSize="small" />Connected to point of sale</div><EmployeeIdentity employee={currentProfile} /></>
                  : <p className="text-sm text-slate-500">No point of sale profile connected.</p>}
              </div>
            </section>

            {isReassigning && !showAssignList && <section>
              <h3 className="text-sm font-semibold text-slate-900">Connected to the wrong person?</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-500">Move this connection to another active employee at this location. You’ll review the change before saving.</p>
              <button type="button" onClick={() => { setShowAssignList(true); setActionStatus(null); }} className={`${secondaryButton} mt-3`}>Choose another employee</button>
            </section>}
            {isArchivedSource && !currentProfile && <p className="text-sm text-slate-500">This archived employee has no connection to move. Open an active employee to connect a point of sale profile.</p>}

            {canChoose && <section>
              <h3 className="text-sm font-semibold text-slate-900">{isReassigning ? "Choose the employee who should receive this connection" : "Choose their point of sale profile"}</h3>
              <p className="mt-1 text-sm text-slate-500">{isReassigning ? "Available employees are active and do not already have a connection." : "Suggested profiles at this location, based on name and email. Verify the details before connecting."}</p>
              {candidates.length > 0 && <>
                <label htmlFor={`${titleId}-search`} className="sr-only">{isReassigning ? "Search available employees" : "Search suggested profiles"}</label>
                <div className="relative mt-4"><SearchIcon className="pointer-events-none absolute left-3 top-3 text-slate-400" fontSize="small" /><input id={`${titleId}-search`} type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by name or email" className="w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-3 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100" /></div>
              </>}
              {filteredCandidates.length > 0 ? <div role="group" aria-label={isReassigning ? "Available employees" : "Suggested profiles"} className="mt-3 max-h-64 space-y-2 overflow-y-auto p-1">
                {filteredCandidates.map((candidate) => {
                  const key = isReassigning ? candidate.id : candidate.uuid;
                  const selected = selection === candidate;
                  return <button key={key} type="button" aria-pressed={selected} onClick={() => { setSelection(candidate); setActionStatus(null); }}
                    className={`flex w-full items-center justify-between gap-3 rounded-xl border p-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600 ${selected ? "border-violet-500 bg-violet-50" : "border-slate-200 hover:border-violet-300 hover:bg-slate-50"}`}>
                    <EmployeeIdentity employee={candidate} />
                    <div className="flex shrink-0 items-center gap-2">{!isReassigning && candidate.email_match && <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 sm:hidden">Same email</span>}<span aria-hidden="true" className={`flex h-5 w-5 items-center justify-center rounded-full border ${selected ? "border-violet-700 bg-violet-700 text-white" : "border-slate-300"}`}>{selected && <span className="h-2 w-2 rounded-full bg-white" />}</span></div>
                  </button>;
                })}
              </div> : <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
                {candidates.length ? "No results. Try another name or email." : isReassigning ? "No available employees were found. The intended employee must be active at this location and have no existing connection." : "No suggested profiles were found. Check the employee’s name and email in your point of sale, then try again after the next sync."}
              </div>}
              {selection && <p role="status" className="mt-3 text-sm text-violet-700">Selected: {employeeName(selection)}</p>}
            </section>}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center justify-end gap-3 border-t border-slate-200 px-6 py-4 sm:px-4">
        <button type="button" disabled={isSaving} onClick={isReviewing ? () => { setIsReviewing(false); setActionStatus(null); } : close} className={secondaryButton}>{isReviewing ? "Back" : "Done"}</button>
        {!error && !isLoading && selection && <button type="button" disabled={isSaving} onClick={isReviewing ? saveConnection : () => setIsReviewing(true)} className={primaryButton}>{isSaving ? "Saving…" : isReviewing ? "Confirm connection" : "Review connection"}</button>}
      </div>
    </Dialog>
  );
}
